import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { RewardPopup } from "@/components/RewardPopup";
import { api } from "@/lib/api";
import { CalendarCheck, Check, UserRound, TrendingUp } from "lucide-react";

const CONSULTANTS = ["Matthew Chen", "Ivano Rossi", "Amy Watson"];
const TIMES = ["9:00 AM", "10:30 AM", "12:00 PM", "1:30 PM", "3:00 PM", "4:30 PM"];
const ENSUITE_REWARD = { name: "Ensuite Upgrade", value: 1200 };

export default function FinanceAppointment() {
  const { session, data, next, back, mergeData, addReward } = useJourney();
  const existing = data.finance_appointment;
  const [consultant, setConsultant] = useState(existing?.consultant || CONSULTANTS[0]);
  const [date, setDate] = useState(existing?.date || "");
  const [time, setTime] = useState(existing?.time || TIMES[1]);
  const [locked, setLocked] = useState(!!existing?.status);
  const [showReward, setShowReward] = useState(false);

  const confirm = () => {
    if (!date) { toast.error("Choose an appointment date."); return; }
    const appt = { consultant, date, time, status: "locked" };
    mergeData({ finance_appointment: appt });
    api.patchSession(session.id, { data: { finance_appointment: appt } }).catch(() => {});
    setLocked(true);
    if (!(data.rewards || []).some((r) => r.name === ENSUITE_REWARD.name)) {
      addReward(ENSUITE_REWARD);
      setShowReward(true);
    }
  };

  return (
    <StageFrame testId="stage-finance-appointment" className="px-6 md:px-14 pt-20 pb-32">
      <div className="max-w-3xl mx-auto">
        <Eyebrow>Section 2 · Finance Appointment</Eyebrow>
        <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-forest mt-3">
          Let's confirm your true finance position.
        </h1>
        <p className="text-mutedText mt-4 max-w-xl">
          Your home is an asset, and growth creates future choices. Before land, floorplans or
          opportunities, a quick chat with a Finance Consultant sets your starting point.
        </p>

        <div className="mt-8 flex items-center gap-2 rounded-xl border border-line bg-white/5 px-4 py-3 text-sm text-forest/80">
          <TrendingUp size={16} className="text-brand" /> Understand your borrowing position — no application, no obligation.
        </div>

        {!locked ? (
          <div className="mt-8 grid gap-5">
            <div>
              <label className="label-eyebrow text-mutedText">Finance Consultant</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {CONSULTANTS.map((c) => (
                  <button key={c} data-testid={`fa-consultant-${c.split(" ")[0]}`} onClick={() => setConsultant(c)}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${consultant === c ? "border-brand bg-brand/15 text-forest" : "border-line text-mutedText"}`}>
                    <UserRound size={14} /> {c}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="label-eyebrow text-mutedText">Date</label>
                <input data-testid="fa-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-line bg-white/5 px-4 py-3 text-forest focus:outline-none focus:ring-2 focus:ring-brand" />
              </div>
              <div>
                <label className="label-eyebrow text-mutedText">Time</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {TIMES.map((t) => (
                    <button key={t} data-testid={`fa-time-${t.replace(/[: ]/g, "")}`} onClick={() => setTime(t)}
                      className={`rounded-full border px-3 py-2 text-sm transition ${time === t ? "border-brand bg-brand/15 text-forest" : "border-line text-mutedText"}`}>{t}</button>
                  ))}
                </div>
              </div>
            </div>
            <button data-testid="fa-confirm-btn" onClick={confirm}
              className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-white text-sm font-medium hover:bg-brand-dark transition">
              <CalendarCheck size={16} /> Confirm appointment
            </button>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
            data-testid="fa-locked" className="mt-8 rounded-2xl border border-brand/40 bg-brand/10 p-7">
            <div className="flex items-center gap-2 text-brand"><Check size={18} /><span className="font-medium">Finance Appointment — Locked In</span></div>
            <p className="mt-3 text-forest text-lg">{consultant}</p>
            <p className="text-mutedText">{date} · {time}</p>
          </motion.div>
        )}
      </div>

      <ConsultantNav testId="stage-finance-appointment" onBack={back} onNext={locked ? next : null} nextLabel="See your roadmap" disabled={!locked} />
      <RewardPopup open={showReward} onClose={() => setShowReward(false)}
        title="FREE Ensuite Upgrade" value={1200}
        subtitle="Added to your rewards — now $2,100 unlocked in total." />
    </StageFrame>
  );
}
